import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Plus, Calendar, Edit, Trash2, Play, Square, Eye, CheckCircle } from 'lucide-react';
import { format } from 'date-fns';
import type { AppDispatch, RootState } from '@/store';
import {
  fetchPlanningPeriods,
  createPlanningPeriod,
  updatePlanningPeriod,
  deletePlanningPeriod,
  activatePlanningPeriod,
  closePlanningPeriod,
  clearError
} from '@/store/slices/planningPeriodsSlice';
import {
  GlassCard,
  GlassButton,
  GlassBadge,
  GlassPageHeader,
  GlassModal,
  GlassConfirmModal,
  GlassLoadingSpinner,
  GlassAlert,
  SectionReveal,
  MetricCard,
  CollapsibleMetricsSection,
} from '@/design-system';
import PlanningPeriodForm from './components/PlanningPeriodForm';
import type { PlanningPeriod, PlanningPeriodStatus } from '@/types';

const PlanningPeriodsPage: React.FC = () => {
  const dispatch = useDispatch<AppDispatch>();
  const { periods, isLoading, isCreating, isUpdating, isDeleting, error } = useSelector(
    (state: RootState) => state.planningPeriods
  );

  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [selectedPeriod, setSelectedPeriod] = useState<PlanningPeriod | null>(null);

  useEffect(() => {
    dispatch(fetchPlanningPeriods());
  }, [dispatch]);

  useEffect(() => {
    if (error) {
      const timer = setTimeout(() => {
        dispatch(clearError());
      }, 5000);
      return () => clearTimeout(timer);
    }
  }, [error, dispatch]);

  const handleCreatePeriod = async (data: Partial<PlanningPeriod>) => {
    const result = await dispatch(createPlanningPeriod(data));
    if (createPlanningPeriod.fulfilled.match(result)) {
      setIsCreateModalOpen(false);
    }
  };

  const handleUpdatePeriod = async (data: Partial<PlanningPeriod>) => {
    if (!selectedPeriod) return;
    const result = await dispatch(updatePlanningPeriod({ id: selectedPeriod.id, data }));
    if (updatePlanningPeriod.fulfilled.match(result)) {
      setIsEditModalOpen(false);
      setSelectedPeriod(null);
    }
  };

  const handleDeletePeriod = async () => {
    if (!selectedPeriod) return;
    const result = await dispatch(deletePlanningPeriod(selectedPeriod.id));
    if (deletePlanningPeriod.fulfilled.match(result)) {
      setIsDeleteModalOpen(false);
      setSelectedPeriod(null);
    }
  };

  const handleActivatePeriod = async (period: PlanningPeriod) => {
    await dispatch(activatePlanningPeriod(period.id));
  };

  const handleClosePeriod = async (period: PlanningPeriod) => {
    await dispatch(closePlanningPeriod(period.id));
  };

  const getStatusBadgeVariant = (status: PlanningPeriodStatus): 'success' | 'warning' | 'default' => {
    switch (status) {
      case 'ACTIVE':
        return 'success';
      case 'DRAFT':
        return 'warning';
      case 'CLOSED':
      default:
        return 'default';
    }
  };

  // Calculate metrics
  const activePeriods = periods.filter(p => p.status === 'ACTIVE').length;
  const draftPeriods = periods.filter(p => p.status === 'DRAFT').length;
  const closedPeriods = periods.filter(p => p.status === 'CLOSED').length;
  const totalStructures = periods.reduce((sum, p) => sum + (p._count?.investibleStructures || 0), 0);

  if (isLoading) {
    return (
      <div className="min-h-screen glass-page-bg flex items-center justify-center">
        <GlassLoadingSpinner size="lg" variant="gradient" label="Loading planning periods..." />
      </div>
    );
  }

  return (
    <div className="min-h-screen glass-page-bg p-6 lg:p-8">
      <SectionReveal>
        <GlassPageHeader
          title="Planning Periods"
          subtitle="Manage annual planning cycles and periods"
          icon={<Calendar className="w-6 h-6" />}
          iconGradient="rose"
          className="mb-6"
          actions={
            <GlassButton
              variant="primary"
              size="sm"
              onClick={() => setIsCreateModalOpen(true)}
              leftIcon={<Plus className="w-4 h-4" />}
            >
              New Planning Period
            </GlassButton>
          }
        />
      </SectionReveal>

      {/* Metrics Row */}
      <SectionReveal delay={0.1}>
        <CollapsibleMetricsSection storageKey="planning-periods">
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
            <MetricCard
              title="Total Periods"
              value={periods.length}
              icon={<Calendar className="w-5 h-5" />}
              iconColor="rose"
            />
            <MetricCard
              title="Active"
              value={activePeriods}
              icon={<Play className="w-5 h-5" />}
              iconColor="emerald"
            />
            <MetricCard
              title="Draft"
              value={draftPeriods}
              icon={<Edit className="w-5 h-5" />}
              iconColor="amber"
            />
            <MetricCard
              title="Total Structures"
              value={totalStructures}
              icon={<CheckCircle className="w-5 h-5" />}
              iconColor="violet"
            />
          </div>
        </CollapsibleMetricsSection>
      </SectionReveal>

      {error && (
        <SectionReveal>
          <GlassAlert
            variant="error"
            message={error}
            onClose={() => dispatch(clearError())}
            className="mb-6"
          />
        </SectionReveal>
      )}

      {/* Planning Periods Grid */}
      <SectionReveal delay={0.2}>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {periods.length === 0 ? (
            <div className="col-span-full text-center py-12">
              <Calendar className="w-12 h-12 text-gray-400 mx-auto mb-4" />
              <h3 className="text-lg font-medium text-gray-500 mb-2">No Planning Periods</h3>
              <p className="text-gray-400">Create your first planning period to get started</p>
            </div>
          ) : (
            periods.map((period) => (
              <GlassCard key={period.id} hover className="flex flex-col">
                <div className="p-5">
                  <div className="flex items-start justify-between mb-4">
                    <div className="flex-1">
                      <h3 className="text-lg font-semibold text-gray-900 mb-1">{period.name}</h3>
                      <p className="text-sm text-gray-500">
                        {format(new Date(period.startDate), 'MMM d, yyyy')} - {format(new Date(period.endDate), 'MMM d, yyyy')}
                      </p>
                    </div>
                    <GlassBadge variant={getStatusBadgeVariant(period.status)}>
                      {period.status.toLowerCase()}
                    </GlassBadge>
                  </div>

                  <div className="grid grid-cols-2 gap-4 mb-6">
                    <div className="text-center p-3 rounded-xl bg-white/50">
                      <div className="text-xl font-bold text-blue-600">
                        {period._count?.investibleStructures || 0}
                      </div>
                      <div className="text-xs text-gray-500 font-medium">Structures</div>
                    </div>
                    <div className="text-center p-3 rounded-xl bg-white/50">
                      <div className="text-xl font-bold text-violet-600">
                        {period._count?.scenarios || 0}
                      </div>
                      <div className="text-xs text-gray-500 font-medium">Scenarios</div>
                    </div>
                  </div>

                  <div className="mb-4">
                    <GlassButton
                      variant="secondary"
                      size="sm"
                      fullWidth
                      leftIcon={<Eye className="w-4 h-4" />}
                    >
                      View Details
                    </GlassButton>
                  </div>

                  <div className="flex items-center justify-between pt-4 border-t border-gray-100">
                    <div className="flex items-center gap-1">
                      <GlassButton
                        variant="ghost"
                        size="sm"
                        onClick={() => {
                          setSelectedPeriod(period);
                          setIsEditModalOpen(true);
                        }}
                        title="Edit"
                      >
                        <Edit className="w-4 h-4" />
                      </GlassButton>
                      <GlassButton
                        variant="ghost"
                        size="sm"
                        onClick={() => {
                          setSelectedPeriod(period);
                          setIsDeleteModalOpen(true);
                        }}
                        title="Delete"
                      >
                        <Trash2 className="w-4 h-4" />
                      </GlassButton>
                    </div>

                    <div className="flex items-center gap-2">
                      {period.status === 'DRAFT' && (
                        <GlassButton
                          size="sm"
                          variant="primary"
                          onClick={() => handleActivatePeriod(period)}
                          leftIcon={<Play className="w-4 h-4" />}
                        >
                          Activate
                        </GlassButton>
                      )}
                      {period.status === 'ACTIVE' && (
                        <GlassButton
                          size="sm"
                          variant="secondary"
                          onClick={() => handleClosePeriod(period)}
                          leftIcon={<Square className="w-4 h-4" />}
                        >
                          Close
                        </GlassButton>
                      )}
                    </div>
                  </div>
                </div>
              </GlassCard>
            ))
          )}
        </div>
      </SectionReveal>

      {/* Floating Create button */}
      <button
        className="fixed bottom-6 right-6 h-14 w-14 rounded-full shadow-lg bg-gradient-to-r from-rose-500 to-rose-600 text-white hover:shadow-xl hover:scale-105 transition-all duration-200 flex items-center justify-center"
        onClick={() => setIsCreateModalOpen(true)}
        aria-label="Create Planning Period"
      >
        <Plus className="w-6 h-6" />
      </button>

      {/* Create Modal */}
      <GlassModal
        isOpen={isCreateModalOpen}
        onClose={() => setIsCreateModalOpen(false)}
        title="Create Planning Period"
        subtitle="Define a new annual planning cycle"
        icon={<Plus className="w-5 h-5" />}
        iconGradient="rose"
        size="md"
      >
        <PlanningPeriodForm
          onSubmit={handleCreatePeriod}
          onCancel={() => setIsCreateModalOpen(false)}
          isLoading={isCreating}
        />
      </GlassModal>

      {/* Edit Modal */}
      <GlassModal
        isOpen={isEditModalOpen}
        onClose={() => {
          setIsEditModalOpen(false);
          setSelectedPeriod(null);
        }}
        title="Edit Planning Period"
        subtitle={selectedPeriod?.name}
        icon={<Edit className="w-5 h-5" />}
        iconGradient="blue-violet"
        size="md"
      >
        <PlanningPeriodForm
          initialData={selectedPeriod}
          onSubmit={handleUpdatePeriod}
          onCancel={() => {
            setIsEditModalOpen(false);
            setSelectedPeriod(null);
          }}
          isLoading={isUpdating}
        />
      </GlassModal>

      {/* Delete Confirmation Modal */}
      <GlassConfirmModal
        isOpen={isDeleteModalOpen}
        onClose={() => {
          setIsDeleteModalOpen(false);
          setSelectedPeriod(null);
        }}
        onConfirm={handleDeletePeriod}
        title="Delete Planning Period"
        message={`Are you sure you want to delete "${selectedPeriod?.name}"? This action cannot be undone.`}
        confirmLabel="Delete"
        variant="danger"
        isLoading={isDeleting}
      />
    </div>
  );
};

export default PlanningPeriodsPage;
